import React from 'react';
import isEqual from 'react-fast-compare';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {UserService} from '../api';
import {Location, UserProfileUpdate} from '../api/types';
import {
  getDebugEmail,
  getFavoriteLocations,
  markMyWeatherOrderSynced,
  selectMyWeatherOrder,
  selectMyWeatherOrderPending,
  selectMyWeatherOrderStationIds,
  selectMyWeatherSyncEnabled,
} from '../store';
import {CommonRootState} from '../store/types';
import {MyWeather} from '../utils';
import useApiClient from './useApiClient';

// Collapse a run of drags and moves into one request.
const ORDER_SYNC_DEBOUNCE_MS = 800;

// Silent: a background save is not the user's business. Never on behalf of
// an account a staff member is logged in as.
const OPTIONS = {silent: true, skipLoginAs: true};

const PLACE_PREFIX = `${MyWeather.PLACE_KIND}:`;

/**
 * Whether the order names a favourite place the sync is still to upload:
 * sent now, the order could not name it (`toServerOrder` leaves it out), and
 * the account would then hold it last. A place the sync never uploads
 * (`toSyncPlace` refuses it) is not waited for, or nothing would ever go.
 */
const awaitsUpload = (order: string[], places: Location[]): boolean => {
  const named = new Set(
    order
      .filter((token) => token.startsWith(PLACE_PREFIX))
      .map((token) => token.slice(PLACE_PREFIX.length)),
  );
  return places.some(
    (place) =>
      !place.serverId &&
      named.has(place.hash) &&
      MyWeather.toSyncPlace(place) !== null,
  );
};

/**
 * Sends a My weather order edited on this device to the account (FE #381).
 * The sync endpoint cannot do it: the server keeps the account's order first
 * there. So the order goes in a profile PATCH, with the station order the
 * edit recorded beside it; without `station_ids` the server would rebuild
 * that one from the order (`mirror_station_ids`), moving owned stations to
 * wherever an older order had them.
 *
 * - Only an edit made here is pending (`editMyWeatherOrder`); an order /me
 *   brought in never is, so it is never echoed back. /me leaves a pending
 *   order alone (`useUserData`).
 * - It waits while the order names a favourite place the sync has still to
 *   upload (`awaitsUpload`); `useMyWeatherSync` uploads it, and this runs
 *   again.
 * - Writes are chained, so the account ends on the last edit; one made while
 *   a request is out stays pending for the next. A write that comes up after
 *   a sign-out or an account switch is dropped.
 * - A failure leaves the edit pending, for the next edit or launch, without
 *   retrying in a loop: nothing the effect reads has changed. LOGOUT drops
 *   the slice, pending edit included.
 * - Never with the sync off (signed out included) or under Login As.
 *
 * Wire this once near the top of the tree (the UserSyncProviders).
 */
const useMyWeatherOrderSync = () => {
  const {fetch} = useApiClient();
  const dispatch = useDispatch();
  const store = useStore<CommonRootState>();
  const enabled = useSelector(selectMyWeatherSyncEnabled);
  const debugEmail = useSelector(getDebugEmail);
  const pending = useSelector(selectMyWeatherOrderPending);
  const order = useSelector<CommonRootState, string[] | null>(
    selectMyWeatherOrder,
    isEqual,
  );
  const stationIds = useSelector<CommonRootState, number[] | null>(
    selectMyWeatherOrderStationIds,
    isEqual,
  );
  const places = useSelector<CommonRootState, Location[]>(
    getFavoriteLocations,
    isEqual,
  );

  // Tail of this hook's chain of writes.
  const lastWriteRef = React.useRef<Promise<unknown>>(Promise.resolve());

  React.useEffect(() => {
    if (!enabled || debugEmail || !pending || !order) {
      return;
    }
    if (awaitsUpload(order, places)) {
      return;
    }
    const update: UserProfileUpdate = {
      my_weather_order: MyWeather.toServerOrder(order, places),
    };
    if (stationIds) {
      update.station_ids = stationIds;
    }
    const sent = {order, stationIds};
    const owner = store.getState().user.email;
    const sameSession = () => {
      const {user} = store.getState();
      return user.token != null && !user.debugEmail && user.email === owner;
    };

    const timer = setTimeout(() => {
      lastWriteRef.current = lastWriteRef.current
        .catch(() => {})
        .then(async () => {
          if (!sameSession()) {
            return;
          }
          await fetch(UserService.updateUserProfile(update), OPTIONS);
          if (sameSession()) {
            dispatch(markMyWeatherOrderSynced(sent));
          }
        })
        .catch(() => {});
    }, ORDER_SYNC_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    enabled,
    debugEmail,
    pending,
    order,
    stationIds,
    places,
    fetch,
    dispatch,
    store,
  ]);
};

export default useMyWeatherOrderSync;
